import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { LoadingButtonComponent } from '../../../../core/components/buttons/loading/loading-button.component';
import { FormErrorsComponent } from '../../../../core/components/form-errors/form-errors.component';
import { InputTextareaComponent } from '../../../../core/components/inputs/input-textarea/input-textarea.component';
import { AG_GRID_DEFAULT_OPTIONS } from '../../../../core/config/aggrid.default.config';
import { BackendErrorService } from '../../../../core/services/backend-error/backend-error.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { ReplaceEmptyStringsWithNull } from '../../../../core/utils/ReplaceEmptyStringsWithNull';
import { EmpresaFacturadoraService } from '../../../../maestros/services/maestros/empresa-facturadora/empresa-facturadora.service';
import { EmpresaFacturadora } from '../../../../models/maestros/empresa-facturadora.model';
import { AppRoutes } from '../../../../shared/route-paths.const';
import { FacturaGastoDtoOut, GastoDtoIn, GastoForm } from '../../../models/gasto.model';
import { ContabilidadGastoService } from '../../../services/contabilidad-gasto.service';

@Component({
  selector: 'app-gasto-new',
  standalone: true,
  imports: [
    FormErrorsComponent,
    LoadingButtonComponent,
    ReactiveFormsModule,
    InputTextareaComponent,
    AgGridAngular,
    CommonModule,
  ],
  templateUrl: './gasto-new.component.html',
  styleUrl: './gasto-new.component.scss',
})
export class GastoNewComponent implements OnInit {
  gridApi!: GridApi;
  isColumnDropdownOpen = false;

  newGastoForm!: ReturnType<typeof this.fb.group<GastoForm>>;
  isLoading = false;
  ultimoAlbaran: number = 0;
  facturasParaGasto: FacturaGastoDtoOut[] = [];

  empresasRefacturadoras: EmpresaFacturadora[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly service: ContabilidadGastoService,
    private readonly toastService: ToastService,
    private readonly backendErrorService: BackendErrorService,
    private readonly empresaFacturadoraService: EmpresaFacturadoraService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.cargarTablasMaestras();
    this.setearSuscripciones();
    this.refreshGridColumns();
  }

  cargarTablasMaestras(): void {
    this.empresaFacturadoraService.setComboEmpresasFacturadoras(this.empresasRefacturadoras);

    this.newGastoForm.get('empresaFacturadoraId')?.setValue(null);
  }

  setearSuscripciones(): void {
    this.newGastoForm.get('empresaFacturadoraId')?.valueChanges.subscribe((value) => {
      if (value) {
        this.cargarFacturasParaGasto(value);
      } else {
        this.facturasParaGasto = [];
        this.toastService.error('Se ha producido un error al cargar las facturas para gasto');
      }
    });
  }

  cargarFacturasParaGasto(idEmpresaFacturadora: number): void {
    this.facturasParaGasto = [];

    this.service.obtenerFacturasParaGasto(idEmpresaFacturadora).subscribe({
      next: (data: FacturaGastoDtoOut[]) => {
        this.facturasParaGasto = data;
      },
    });
  }

  crearGasto(): void {
    this.isLoading = true;
    const rawGasto = this.newGastoForm.getRawValue();
    const gasto: GastoDtoIn = ReplaceEmptyStringsWithNull.clean(rawGasto);

    this.service.crearGasto(gasto).subscribe({
      next: (data) => {
        this.isLoading = false;
        this.toastService.success('Gasto creado correctamente');
        this.router.navigate([`${AppRoutes.CONTABILIDAD_ONDA.GASTOS}/${data.id}`]);
      },
      error: (err) => {
        this.isLoading = false;
        this.handleCreateError(err);
      },
    });
  }

  private handleCreateError(err: any): void {
    if (err?.error?.errors) {
      this.backendErrorService.handleBackendErrors(err.error.errors, this.newGastoForm);
    } else {
      const errorMessage = err?.error?.message ?? 'Contacte con el administrador';
      this.toastService.error(`Error al crear la Factura de Suministro: ${errorMessage}`);
    }
  }

  initForm(): void {
    this.newGastoForm = this.fb.group<GastoForm>({
      empresaFacturadoraId: this.fb.control(null),
      fechaAsientoContable: this.fb.control(null),
      codAlbaranInicial: this.fb.control(null),
      observaciones: this.fb.control(null),
      facturas: this.fb.control([], { nonNullable: true }),
    });
  }

  gridOptions: GridOptions = {
    ...AG_GRID_DEFAULT_OPTIONS,
    domLayout: 'autoHeight',
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    onSelectionChanged: () => this.onSelectionChanged(),
  };

  columnDefs: ColDef[] = [];

  columnOptions = [
    { field: 'proveedorNombre', headerName: 'Proveedor', selected: true },
    { field: 'codFactura', headerName: 'Numero', selected: true },
    { field: 'fechaEmision', headerName: 'FechaEmision', selected: true, filter: 'agDateColumnFilter' },
    { field: 'baseImponible', headerName: 'Base Imponible', selected: true },
    { field: 'importeTotal', headerName: 'Importe Total', selected: true },
    { field: 'numeroFacturaCompra', headerName: 'Numero Compra', selected: true },
    { field: 'fechaFacturaCompra', headerName: 'Fecha Compra', selected: true },
    { field: 'cuentaBancariaCompra', headerName: 'Cuenta Bancaria Compra', selected: true },
  ];

  onSelectionChanged(): void {
    const selectedNodes = this.gridApi.getSelectedNodes() || [];
    const selectedIds = selectedNodes.map((node) => node.data.id);
    this.newGastoForm.controls.facturas.setValue(selectedIds);
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
  }

  toogleColumnDropdown(): void {
    this.isColumnDropdownOpen = !this.isColumnDropdownOpen;
  }

  toggleColumnaSeleccionada(option: any): void {
    option.selected = !option.selected;
    this.refreshGridColumns();
  }

  refreshGridColumns(): void {
    const id = {
      headerName: '',
      field: 'checkbox',
      width: 50,
      pinned: 'left' as const,
      checkboxSelection: true,
      headerCheckboxSelection: true,
    };

    this.columnDefs = [
      id,
      ...this.columnOptions.map((col) => ({
        field: col.field,
        headerName: col.headerName,
        filter: col.filter,
        hide: this.isColumnHide(col.field),
      })),
    ];
  }

  isColumnHide(columna: string): boolean {
    const column = this.columnOptions.find((col) => col.field === columna);
    return !column?.selected;
  }
}
